import React, { useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, MODAL_TYPES, IModalBaseProps } from 'transfix-ui/components/Modals'
import { makeStyles, Typography } from 'transfix-ui/core'
import { IFacilityReviewStatuses } from 'types/graphqlTypes'
import { capitalize } from 'utils/capitalize'
import { MergeDetails } from '../../../../pages/facilities/components/MergeFacilitiesModals/components/MergeDetails'
import { TStep } from '../ActionsSelector'
import { EdiMergeFacilityStep } from '../EdiMergeFacilityStep'
import { TAction } from '../FacilitySelector'
import { IFacilitySummaryProps } from '../FacilitySummary'
import { VendorForm } from '../VendorForm'
import { useVendorForm } from '../VendorForm/useVendorFormContext'
import { useEdiMergeFacilityInfoQuery } from './graphql/EdiMergeFacilityInfo'
import { useReviewFacility, useVendorFormAction } from './utils'

export interface IEdiMergeFacilityModalProps extends Omit<IModalBaseProps, 'modalType'> {
  shipmentId?: string
  shipperFacilityUuid: string
  shipperApplicationId?: string
  onSuccess?: () => void
}

const useEdiMergeFacilityModalStyles = makeStyles(theme => ({
  additionalFooter: {
    margin: 'auto',
  },
  body: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}))

const openFacilityTab = (uuid: string) => {
  window.open(`${__ROOT__}/facilities/${uuid}`, '_blank')
}

export const EdiMergeFacilityModal = ({
  onClose,
  onSuccess = () => {},
  shipmentId,
  shipperApplicationId,
  shipperFacilityUuid,
  ...props
}: IEdiMergeFacilityModalProps) => {
  const styles = useEdiMergeFacilityModalStyles()
  const hasShipmentId = Boolean(shipmentId)

  const { data } = useEdiMergeFacilityInfoQuery({
    variables: {
      shipmentId: shipmentId || '',
      shipperFacilityUuid,
      includeShipment: hasShipmentId,
    },
  })
  const { facility: originalFacility, shipment } = data || {}
  const { shipperApplication, stops = [] } = shipment || {}
  const { stopType } =
    stops.find(({ shipperFacility }) => shipperFacility.uuid === shipperFacilityUuid) || {}
  const [step, setStep] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [facility, setFacility] = useState<IFacilitySummaryProps['facility']>()
  const [addVendorStep, setAddVendorStep] = useState<boolean>(false)
  const [action, setAction] = useState<TStep>()
  const [facilityAction, setFacilityAction] = useState<TAction>()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const methods = useVendorForm({
    defaultValues: {},
  })
  const { setVendor, vendorForm } = useVendorFormAction()
  const { reviewFacility } = useReviewFacility()

  const steps = useMemo(
    () => [
      {
        component: (
          <EdiMergeFacilityStep
            key='selectFacility'
            shipmentId={shipmentId}
            shipperApplicationId={shipperApplicationId}
            shipperFacilityUuid={shipperFacilityUuid}
            onFacilityChange={(action, selectedFacility) => {
              setFacilityAction(action)
              setFacility(selectedFacility || undefined)
            }}
            onActionChange={(action, addVendor) => {
              setAction(action)
              setAddVendorStep(addVendor)
            }}
            defaultAction={action}
            defaultAddVendor={addVendorStep}
            defaultFacility={facility}
            defaultFacilityAction={facilityAction}
            disableMergeAction={facility?.uuid === originalFacility?.uuid}
          />
        ),
        title: hasShipmentId
          ? `Review ${capitalize(stopType || '')} Address for ${
              shipperApplication?.name || ''
            } #${shipmentId}`
          : 'Review New Duplicate Facility Address',
        nextButton: {
          text: 'Next',
          action: async () => {
            setErrorMessage(null)
            if (facilityAction === 'other' && !facility) {
              setErrorMessage('Please select a facility')
              return
            }
            if (!action) {
              setErrorMessage('Please select an option to handle this address')
              return
            }
            if (action === IFacilityReviewStatuses.retained_as_duplicate) {
              setLoading(true)
              const [reviewResult, reviewError] = await reviewFacility({
                decision: action,
                facilityUuid: originalFacility?.uuid || '',
                mergeIntoFacilityUuid:
                  originalFacility?.uuid === facility?.uuid ? undefined : facility?.uuid,
              })
              setLoading(false)

              if (reviewResult === 'error' && reviewError) {
                setErrorMessage(reviewError)
                return
              }

              onSuccess()
              onClose()
            } else {
              setStep(step + 1)
            }
          },
        },
        backButton: {
          text: 'Cancel',
          action: onClose,
        },
      },
      ...(addVendorStep && IFacilityReviewStatuses.merged_to_primary
        ? [
            {
              component: <VendorForm mutationError={null} facility={facility} />,
              title: 'Add Vendor',
              nextButton: {
                text: 'Next',
                action: methods.handleSubmit(data => {
                  methods.reset(data)
                  setVendor(data)
                  setStep(step + 1)
                }),
              },
              backButton: {
                text: 'Previous',
                action: () => setStep(step - 1),
              },
            },
          ]
        : []),
      ...(action === IFacilityReviewStatuses.merged_to_primary
        ? [
            {
              component: <MergeDetails key='details' facility={facility} />,
              title: 'Merge a Duplicate Facility into this Facility',
              nextButton: {
                text: 'Save',
                action: async () => {
                  setLoading(true)
                  const [vendorResult, vendorError] = await vendorForm(facility)

                  if (vendorResult === 'error' && vendorError) {
                    setStep(1)
                    setErrorMessage(vendorError)
                    setLoading(false)
                    return
                  }

                  const [reviewResult, reviewError] = await reviewFacility({
                    decision: action,
                    facilityUuid: originalFacility?.uuid || '',
                    mergeIntoFacilityUuid:
                      originalFacility?.uuid === facility?.uuid ? undefined : facility?.uuid,
                  })
                  setLoading(false)

                  if (reviewResult === 'error' && reviewError) {
                    setErrorMessage(reviewError)
                    return
                  }

                  openFacilityTab(facility?.uuid || '')
                  onSuccess()
                  onClose()
                },
              },
              backButton: {
                text: 'Previous',
                action: () => setStep(step - 1),
              },
            },
          ]
        : []),
    ],
    [
      shipmentId,
      shipperApplicationId,
      shipperFacilityUuid,
      facility,
      hasShipmentId,
      stopType,
      shipperApplication?.name,
      step,
      addVendorStep,
      action,
      onClose,
      facilityAction,
      methods,
      originalFacility,
      reviewFacility,
      setVendor,
      vendorForm,
      onSuccess,
    ]
  )

  const { backButton, component, nextButton, title } = steps[step]

  return (
    <ModalBase
      dataTest='EdiMergeFacilityModal'
      modalType={MODAL_TYPES.form}
      size={5}
      title={title}
      onClose={onClose}
      actions={[
        {
          label: backButton.text,
          type: 'dismiss',
          action: backButton.action,
        },
        {
          label: nextButton.text,
          type: 'confirm',
          action: nextButton.action,
          disabled: loading,
        },
      ]}
      bodyClassName={styles.body}
      additionalFooterInfo={
        steps.length > 1 ? (
          <div className={styles.additionalFooter}>
            <Typography color='textSecondary' variant='body2' component='span'>
              {step + 1} of {steps.length}
            </Typography>
          </div>
        ) : undefined
      }
      {...props}>
      {errorMessage && (
        <InlineMessage message={errorMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
      )}
      <FormProvider {...methods}>{component}</FormProvider>
    </ModalBase>
  )
}
